import { addProductLikeBatch } from '../likes/api';
import { picturePreviewAdapter } from '../pictures/helpers';
import { type DisplayPrice } from '../price/aliases';
import { type PresApiSearchProduct } from '../search/aliases';
import { safeJsonParse } from '../storage/helpers';

import { GUEST_LIKED_PRODUCTS_STORAGE_LIMIT } from './constants';
import {
  clearStoredGuestLikes,
  getStoredGuestLikes,
  setStoredGuestLikes,
} from './store';
import { type GuestLikedProduct, type GuestLikeProductInput } from './types';

export function toGuestLikeProduct(product: {
  id: number;
  slug: string;
  pictures?: PresApiSearchProduct['pictures'];
  pricing: { display_price: DisplayPrice };
  brand_name?: string;
  sizes?: { name?: string; status: string }[];
  imageAlt: string;
}): GuestLikeProductInput {
  return {
    id: product.id,
    slug: product.slug,
    pictures: product.pictures?.slice(0, 2).map(picturePreviewAdapter) ?? [],
    displayPrice: product.pricing.display_price,
    brandName: product.brand_name,
    sizes: product.sizes?.flatMap(({ name, status }) =>
      status === 'STATUS_ONSALE' && name ? [name] : []
    ),
    imageAlt: product.imageAlt,
  };
}

export function getGuestLikes(): GuestLikedProduct[] {
  return safeJsonParse(getStoredGuestLikes(), []);
}

export function removeGuestLike(productId: number) {
  setStoredGuestLikes(
    JSON.stringify(getGuestLikes().filter(({ id }) => id !== productId))
  );
}

export function addGuestLike(product: GuestLikedProduct) {
  const guestLikes = getGuestLikes();
  if (guestLikes.length >= GUEST_LIKED_PRODUCTS_STORAGE_LIMIT) {
    return 'reachedCap';
  }
  setStoredGuestLikes(JSON.stringify([...guestLikes, product]));
  return guestLikes.length + 1 === GUEST_LIKED_PRODUCTS_STORAGE_LIMIT
    ? 'lastLike'
    : 'added';
}

export function clearGuestLikes() {
  clearStoredGuestLikes();
}

export async function syncGuestLikes() {
  const likes = getGuestLikes();
  if (likes.length === 0) {
    return 0;
  }
  let syncedCount = 0;
  try {
    await addProductLikeBatch(likes.map((item) => item.id));
    syncedCount = likes.length;
  } catch {
    // fail silently
  }
  clearGuestLikes();
  return syncedCount;
}
